import React, { useState } from 'react';
import { ThemeProvider } from '@emotion/react';
import { Link } from 'react-router-dom';
import { Text, TextVariant } from '@naf/text';
import { themeLight } from '@lib/nafTheme';
import { nafColor } from '@nafcore/theme';
import { colors } from '@naf-simba/theme';
import { VehicleTestData } from '@appTypes/showcaseBlockType';
import { testTabItem } from '@components/bilguide/aboutCar/AboutCar';
import { ElPrixContextData, Season } from '@appTypes/ElPrixContextData';
import { formatTestSeason } from '@pages/Elbilguiden/RangeTests';
import ResponsiveImage from '@components/image/ResponsiveImage';
import { calculateChange } from './calculateChange';
import { HoverWrap } from '../../blocks/MultiRowArticleBlock';
import { Field } from '../VehicleTestResultField';
import * as S from './CombinedResultCard.styles';

export type Variant = 'card' | 'result';

interface Props {
  data: VehicleTestData;
  context: Pick<ElPrixContextData, 'season' | 'testDate' | 'tests'>;
  variant?: Variant;
  refMap?: () => Map<any, any>;
  refMapKey?: string;
  anchorLink?: string;
}

export type CustomTheme = {
  bg: string;
  hover: string;
  bgBadge: string;
  badgeTextColor: string;
};

const getTheme = (season: Season, variant: Variant): CustomTheme => {
  const theme: CustomTheme = {
    bg: colors.secondary[50],
    hover: colors.secondary[100],
    bgBadge: colors.secondary[900],
    badgeTextColor: colors.secondary[50],
  };
  if (variant === 'card') {
    return theme;
  }
  if (season === 'summer') {
    const summerSurface = colors.secondary[50];
    theme.bgBadge = summerSurface;
    theme.hover = summerSurface;
    theme.badgeTextColor = nafColor.signature.black;
    return theme;
  }
  theme.bg = nafColor.information.informationLight;
  theme.hover = nafColor.information.informationLight;
  theme.bgBadge = nafColor.information.informationLight;
  theme.badgeTextColor = nafColor.signature.black;
  return theme;
};

/*
 ! This Card completely rewrites what Card from design system provides. We should create new solid foundations for cards in nafno
 I think we should create one, customizable Card component with blocks to build what we need in specific use case
 */
export const CombinedResultCard: React.FC<Props> = ({
  data,
  context,
  variant = 'card',
  refMap,
  refMapKey,
  anchorLink,
}) => {
  const { name, testData, categories, slug, vehicleId } = data;
  const { image, rangeTest, chargeTest } = testData ?? {};
  const { season, testDate, tests } = context;
  const [theme] = useState(getTheme(season, variant));
  const [sortedCategories] = useState([...(categories || [])].sort());
  const maxMeasuredChargeEffect = Math.max(...Object.values(chargeTest?.readings ?? {}), -Infinity);
  const isCardVariant = variant === 'card';
  const isResultVariant = variant === 'result';
  const vehicleSlug = typeof slug === 'string' ? slug : slug?.current;

  let link = `/bilguiden/bilmodell/${vehicleSlug}?tab=${testTabItem}`;
  if (anchorLink) {
    link = `${link}#${anchorLink}`;
  }

  const isLink = !!vehicleSlug;

  return (
    <ThemeProvider theme={{ ...themeLight, ...theme }}>
      <S.Card
        $variant={variant}
        as={isLink ? Link : 'div'}
        to={isLink ? link : undefined}
        ref={(node) => {
          if (!refMap) return null;
          const map = refMap();
          if (node) {
            map.set(`${refMapKey}_${vehicleId}`, node);
          }
          return null;
        }}
      >
        {isResultVariant && (
          <S.NafBadge layoutVariant="result" variant={TextVariant.TagSmall}>
            el prix {formatTestSeason(season, testDate)}
          </S.NafBadge>
        )}
        {isCardVariant && (
          <S.ImageContainer $variant={variant}>
            {image && <ResponsiveImage imageId={image?.publicId} altText={image?.altText ?? ''} quality="auto:eco" />}
          </S.ImageContainer>
        )}
        <S.InfoContainer $variant={variant}>
          <S.Top $variant={variant}>
            {categories && isCardVariant ? (
              <Text variant={TextVariant.TagSmall}>
                {sortedCategories
                  .sort((a, b) => a.category.localeCompare(b.category))
                  .map(({ category }) => category)
                  .join(', ')}
              </Text>
            ) : (
              <div style={{ height: isCardVariant ? 28 : 0 }} />
            )}
            <S.Header layoutVariant={variant} variant={TextVariant.Header2}>
              <HoverWrap>{name}</HoverWrap>
            </S.Header>
          </S.Top>
          <S.DataContainer $variant={variant}>
            <S.StatedInfo $variant={variant}>
              {isResultVariant && (
                <div>
                  <Text variant={TextVariant.TagSmall}>Oppgitt for testbil</Text>
                </div>
              )}
              {(!tests || tests.range) && rangeTest && (
                <Field
                  label="Rekkevidde (WLTP)"
                  value={rangeTest.statedRangeKm}
                  unit="km"
                  explicitlyMissing={rangeTest.statedRangeKm === 0}
                />
              )}
              {isResultVariant && tests && tests.consumption && rangeTest ? (
                <Field
                  label="Forbruk (WLTP)"
                  value={rangeTest.statedConsumptionKWhper100km}
                  unit="kWh"
                  explicitlyMissing={rangeTest.statedConsumptionKWhper100km === 0}
                />
              ) : null}
              {(!tests || tests.chargeTime) && chargeTest && (
                <Field
                  label="Ladetid (10&#8211;80%)"
                  value={chargeTest.statedTimeTo80PercentMinutes}
                  unit="min"
                  explicitlyMissing={chargeTest.statedTimeTo80PercentMinutes === 0}
                />
              )}
              {tests?.maxEffect && (
                <Field
                  label="Maks effekt"
                  value={chargeTest.maxChargeEfficiency}
                  unit="kW"
                  explicitlyMissing={chargeTest.maxChargeEfficiency === 0}
                />
              )}
            </S.StatedInfo>
            <S.TestedInfo $variant={variant}>
              {isResultVariant && (
                <div>
                  <Text variant={TextVariant.TagSmall}>Målt av NAF</Text>
                </div>
              )}
              {isCardVariant && (
                <S.NafBadge layoutVariant="card" variant={TextVariant.TagSmall}>
                  el prix {formatTestSeason(season, testDate)}
                </S.NafBadge>
              )}
              {(!tests || tests.range) && rangeTest && (
                <Field
                  label="Rekkevidde"
                  value={rangeTest.measuredRangeKm}
                  unit="km"
                  difference={calculateChange({
                    stated: rangeTest.statedRangeKm,
                    measured: rangeTest.measuredRangeKm,
                    increaseIsPositive: true,
                    scheme: 'percentage',
                  })}
                  explicitlyMissing={rangeTest.measuredRangeKm === 0}
                  explicitlyMissingLabel="Ikke målt"
                />
              )}
              {isResultVariant && tests && tests.consumption && rangeTest ? (
                <Field
                  label="Forbruk (WLTP)"
                  value={rangeTest.measuredConsumptionKWhper100km}
                  unit="kWh"
                  explicitlyMissing={rangeTest.measuredConsumptionKWhper100km === 0}
                />
              ) : null}
              {tests?.distanceAt10PercentSoc && rangeTest && (
                <Field
                  label="Km kjørt v/ 10%"
                  value={rangeTest.measuredDistanceAt10PercentSoc}
                  unit="km"
                  explicitlyMissing={rangeTest.measuredDistanceAt10PercentSoc === 0}
                  explicitlyMissingLabel="Ikke målt"
                />
              )}
              {(!tests || tests.chargeTime) && chargeTest && (
                <Field
                  label="Ladetid (10&#8211;80%)"
                  value={chargeTest.measuredTimeTo80PercentMinutes}
                  unit="min"
                  difference={calculateChange({
                    stated: chargeTest.statedTimeTo80PercentMinutes,
                    measured: chargeTest.measuredTimeTo80PercentMinutes,
                    increaseIsPositive: false,
                    scheme: 'difference',
                  })}
                  explicitlyMissing={chargeTest.measuredTimeTo80PercentMinutes === 0}
                  explicitlyMissingLabel="Ikke målt"
                />
              )}
              {tests?.maxEffect && maxMeasuredChargeEffect !== -Infinity && (
                <Field
                  label="Maks effekt"
                  value={maxMeasuredChargeEffect}
                  unit="kW"
                  difference={calculateChange({
                    stated: chargeTest.maxChargeEfficiency,
                    measured: maxMeasuredChargeEffect,
                    increaseIsPositive: true,
                    scheme: 'percentage',
                  })}
                  explicitlyMissing={maxMeasuredChargeEffect === 0}
                  explicitlyMissingLabel="Ikke målt"
                />
              )}
            </S.TestedInfo>
          </S.DataContainer>
        </S.InfoContainer>
      </S.Card>
    </ThemeProvider>
  );
};
